import { apiClient } from "@/services/api"
import { defineStore } from "pinia"
import { useAuthStore } from "@/store/authentication"
import { useRecentProjectsStore } from "@/store/recentProjects.js"
import { applyTerminology } from "@/plugins/i18n"

/**
 * The sender's details the PDF-Vorlagen' Briefpapiere print (`{{company.iban}}` …), all optional.
 * Keep in sync with the backend `TenantDocumentDetails`.
 */
export const blankDocumentDetails = () => ({
  phone: null,
  email: null,
  website: null,
  managingDirector: null,
  registerCourt: null,
  registerNumber: null,
  vatId: null,
  taxNumber: null,
  bankName: null,
  iban: null,
  bic: null,
})

export const useSettingsStore = defineStore("settings", {
  state: () => ({
    settings: {
      apiVersion: "",
      currency: "EUR",
      street: "",
      houseNumber: "",
      postcode: "",
      place: "",
      state: "",
      country: "",
      currencyLocale: "de-DE",
      defaultUserRole: "",
      weekStartDay: "MONDAY",
      // Tenant-wide default employer's social-security contribution surcharge (Arbeitgeberanteil), in
      // percent, applied to labour cost in the running post-calculation. 0 = no surcharge.
      employerContributionPercent: 0,
      // What this workspace calls its reports, as overrides of the defaults: one entry per renamed
      // record and language ({ term, language, singular, plural, gender }). See services/terminology.js.
      terminology: [],
      // Whether the app sends a Tagesmeldung only with where the device was; on unless turned off.
      daySubmissionLocationRequired: true,
      // Whether the app clocks in and sends a crew day only with the location; on unless turned off.
      // Clocking out never waits for it.
      timeEntryLocationRequired: true,
      documentDetails: blankDocumentDetails(),
    },
  }),
  getters: {
    getSettings: state => {
      return {
        ...state.settings,
        // TODO: API returns locale with underscore, but it is needed with dash for frontend
        //  e.g. in LewLocaleSelect & LewCurrencyInput
        currencyLocale: state.settings.currencyLocale.replace(/_/g, "-"),
      }
    },
  },
  actions: {
    setSettings(settings) {
      this.settings = settings
      // Every text that names a report is resolved against these, so they follow every change of
      // hands — including sign-out (null), which puts the default names back.
      applyTerminology(settings?.terminology)
    },
    updateSettings(settings) {
      return apiClient.put("/settings", settings, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          this.setSettings(res.data)
        }
        return res
      })
    },
    loadSettings() {
      return apiClient
        .get("/settings", { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setSettings(res.data)
          }
          return res
        })
        .catch(error => {
          if (error.response?.status === 403) {
            const authStore = useAuthStore()
            const recentProjectStore = useRecentProjectsStore()
            authStore.setTenant("")
            authStore.clearToken()
            recentProjectStore.clearRecentProjects()
          }
        })
    },
  },
})
